export const BLOCKED_ATTACHMENT_EXTENSIONS = [
    'exe', 'bat', 'cmd', 'msi', 'app', 'dmg', 'jar',
] as const

export const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024
export const MAX_TASK_ATTACHMENTS = 30

// Examples for error messages, not an allowlist: other non-executable formats are accepted too.
export const SUPPORTED_ATTACHMENT_FORMATS = [
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'heic',
    'pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'md',
    'xls', 'xlsx', 'ods', 'csv', 'tsv',
    'ppt', 'pptx', 'odp', 'key',
    'mp4', 'mov', 'webm', 'avi', 'mkv',
    'mp3', 'wav', 'ogg', 'm4a',
    'zip', 'rar', '7z', 'tar', 'gz',
    'json', 'xml', 'yaml', 'js', 'py', 'java', 'html', 'css', 'sql', 'log',
    'psd', 'ai', 'fig', 'sketch', 'xd',
] as const

const attachmentFormatsHint = `Разрешённые форматы: ${SUPPORTED_ATTACHMENT_FORMATS.join(', ')} и другие, кроме исполняемых файлов (${BLOCKED_ATTACHMENT_EXTENSIONS.join(', ')}).`

const blockedExtensions = new Set<string>(BLOCKED_ATTACHMENT_EXTENSIONS)
type AttachmentFile = Pick<File, 'name'> & Partial<Pick<File, 'size'>>

// This is a picker check. The server validates the actual file content.
export const isAllowedAttachment = (file: Pick<File, 'name'>) => {
    const extension = file.name.trim().split('.').pop()?.toLowerCase()
    return !extension || !blockedExtensions.has(extension)
}

export const getUnsupportedAttachmentMessage = (files: AttachmentFile[]) => {
    const unsupported = files.filter((file) => !isAllowedAttachment(file))
    if (!unsupported.length) return null

    return `Файлы ${unsupported.map((file) => file.name).join(', ')} не поддерживаются. ${attachmentFormatsHint}`
}

export const getAttachmentValidationMessage = (files: AttachmentFile[], existingCount = 0) => {
    const unsupportedMessage = getUnsupportedAttachmentMessage(files)
    if (unsupportedMessage) return unsupportedMessage

    const oversized = files.filter((file) => (file.size || 0) > MAX_ATTACHMENT_SIZE)
    if (oversized.length) {
        return `Файлы ${oversized.map((file) => file.name).join(', ')} превышают максимальный размер 50 MB на файл.`
    }

    if (existingCount + files.length > MAX_TASK_ATTACHMENTS) {
        return `Можно прикрепить максимум ${MAX_TASK_ATTACHMENTS} файлов. Уже добавлено: ${existingCount}, выбрано: ${files.length}.`
    }

    return null
}

const getApiMessage = (data: unknown): string | null => {
    if (typeof data === 'string') return data.trim() || null
    if (Array.isArray(data)) return data.map(getApiMessage).filter(Boolean).join('; ') || null
    if (!data || typeof data !== 'object') return null

    const fields = data as Record<string, unknown>
    return getApiMessage(fields.detail) || getApiMessage(fields.message)
        || getApiMessage(fields.msg) || getApiMessage(fields.error) || getApiMessage(fields.errors)
}

export const getAttachmentUploadErrorMessage = (
    error: unknown,
    fileName?: string,
    fallback = 'Не удалось загрузить файл. Попробуйте снова.',
) => {
    const apiError = error as { response?: { data?: unknown; status?: number }; data?: unknown; status?: number; message?: string } | null
    const status = apiError?.response?.status ?? apiError?.status
    const reason = status === 413
        ? 'Превышен допустимый размер загрузки. Максимум 50 MB на файл.'
        : getApiMessage(apiError?.response?.data ?? apiError?.data)
            || apiError?.message || fallback

    if (!fileName) return reason
    const message = `Не удалось загрузить файл «${fileName}»: ${reason}`
    return reason.includes(attachmentFormatsHint) ? message : `${message}\n${attachmentFormatsHint}`
}
